import React from "react";

// Import css styling
import "@fortawesome/fontawesome-free/css/all.css";
import "bootstrap/dist/css/bootstrap.css";
import "../gearshiftGame/styles/cursor.css";
import "../gearshiftGame/styles/hud.css";
import "../gearshiftGame/styles/laneLabels.css";
import "../gearshiftGame/styles/gameBoard.css";
import "../gearshiftGame/styles/utility.css";
import "../gearshiftGame/styles/asyncLoader.css";
import "../experiment/procedures/hq/hqPanels.css";
import "./index.css";

// Import configurations and utilities
import { config } from "../config/main";
import * as trigger from "../config/trigger";
import { loadExternalJSON } from "../lib/utils";
import localMetadata from "../config/metadata.json";
import localMetadata0 from "../config/metadata.0.json";
import localMetadata0RL from "../config/metadata.0.rl.json";
import localMetadata0AiAgent from "../config/metadata.0.aiagent.json";
import localMetadata1 from "../config/metadata.1.json";
import localMetadata2 from "../config/metadata.2.json";
import localMetadata3 from "../config/metadata.3.json";
import localMetadata3RL from "../config/metadata.3.rl.json";
import localMetadata4 from "../config/metadata.4.json";
import localMetadata5 from "../config/metadata.5.json";
import { stripDebugOnlyKeyboardFields } from "../lib/keyboardUtils";

// Function to get the correct metadata based on mission number
function getLocalMetadata() {
  const missionNumber = parseInt(process.env.REACT_APP_MISSION_NUMBER || "1", 10);
  // Points mission 0 at the local missionIsAdaptive test config (public/testConfigs/mission0_adaptive.json)
  const useRLExperiment = process.env.REACT_APP_RL_EXPERIMENT === "true";
  // Points mission 0 at the local AI-agent test config (public/testConfigs/mission0_ai_agent.json) -
  // its own metadata so the AI-agent dev flow never depends on the real remote task file, see
  // src/experiment/procedures/aiAgent/README.md.
  const useAiAgentMode = process.env.REACT_APP_AI_AGENT_MODE === "true";
  console.log(
    `Loading metadata for mission ${missionNumber}${useRLExperiment ? " (RL experiment)" : ""}${useAiAgentMode ? " (AI agent)" : ""}`
  );

  switch (missionNumber) {
    case 0:
      if (useRLExperiment) return localMetadata0RL;
      if (useAiAgentMode) return localMetadata0AiAgent;
      return localMetadata0;
    case 1:
      return localMetadata1;
    case 2:
      return localMetadata2;
    case 3:
      return useRLExperiment ? localMetadata3RL : localMetadata3;
    case 4:
      return localMetadata4;
    case 5:
      return localMetadata5;
    default:
      return localMetadata;
  }
}

// Fallback values used when required metadata fields are missing.
const REQUIRED_METADATA_FALLBACKS = {
  pilot_version: "_vGF02_MM_DemoShort",
  this_metadata_file_name:
    "https://backend.bgbehavior.com/gs01_task/gs01_metadata_vGF02_MM_DemoShort.json",
  build_date_time: "Wed Nov 12 19:03:16 2025",
  pulseTTLtrialStart: false, // safe default for metadata files that predate this field
  URL_postGame: "https://brown.co1.qualtrics.com/jfe/form/SV_aUYTG1F2t7qV49w",
  target_distance: 100,
  target_speed: 1,
  driver_speed: 5,
  packageSpeed: 2,
  driverHorizontalSpeed: 6,
  target_appearance: 100,
  maxTime_stimulusDuration: 3500,
  maxDriverSpeed: 8,
  minDriverSpeed: 1,
  speedIncrement: 1,
  targetLaunchDelay: 0,
  collisionDelay: 3000,
  headquarterMessageDuration: null,
  feedbackDuration: 500,
  driverProposeTruckDuration: 1000,
  rewardReductionByReset: 5,
  truckMoveDistance: 500,
};

/**
 * Validates the metadata structure and content
 * @param {Object} metadata The metadata object to validate
 * @returns {Object} { isValid: boolean, error: string|null, normalizedMetadata?: Object, missingFields?: string[] }
 */
function validateMetadata(metadata) {
  // Check if missionTaskName exists and is an array
  if (!metadata.missionTaskName || !Array.isArray(metadata.missionTaskName)) {
    return {
      isValid: false,
      error: "Invalid metadata: missionTaskName structure is missing or invalid",
    };
  }

  // Require at least 6 mission taskName entries
  if (metadata.missionTaskName.length < 6) {
    return {
      isValid: false,
      error: "Invalid metadata: missionTaskName must contain at least 6 entries",
    };
  }

  // Check if at least one mission has a taskName with content
  const hasAnyTaskName = metadata.missionTaskName.some(
    (mission) => mission.taskName && Array.isArray(mission.taskName) && mission.taskName.length > 0
  );

  if (!hasAnyTaskName) {
    return {
      isValid: false,
      error: "Invalid metadata: No mission configurations found. All taskName arrays are empty.",
    };
  }

  // Ensure required fields exist; apply fallback values for any missing field.
  const requiredFields = Object.keys(REQUIRED_METADATA_FALLBACKS);
  const missingFields = requiredFields.filter((field) => !(field in metadata));

  const normalizedMetadata = {
    ...REQUIRED_METADATA_FALLBACKS,
    ...metadata,
  };

  if (missingFields.length > 0) {
    console.log(
      `Metadata missing required fields (${missingFields.join(", ")}). Applying fallback values.`
    );
  }

  return {
    isValid: true,
    error: null,
    normalizedMetadata,
    missingFields,
  };
}

// Import deployment functions
import { METADATA_FILE_URL } from "../gearshiftGame/constants";
import { addToFirebase, validateParticipant, fetchMetadataUrl } from "./deployments/firebase";

// Import React components
import JsPsychExperiment from "./components/JsPsychExperiment";
import Login from "./components/Login";

/**
 * The top-level React component for Ecosystem. App handles initiating the jsPsych component when the participant
 * successfully logs in, and stores the overall state of the experiment. Importantly, App keeps track of what the
 * experiment is running on (Electron or the Browser/Firebase).
 *
 * Note that App is a functional component, which means it uses React callbacks rather than class methods. Learn more
 * about functional vs. class components here: https://reactjs.org/docs/components-and-props.html. It is recommended
 * to use functional components.
 */
export default function App() {
  // Manage if a user is currently logged in
  const [loggedIn, setLoggedIn] = React.useState(false);
  // Manage the user's login information
  const [loginInformation, setLoginInformation] = React.useState();

  // Manage the method type being used ("desktop", "firebase", or "default")
  const [currentMethod, setMethod] = React.useState("default");

  // The metadata file used to build the experiment
  const [metadata, setMetadata] = React.useState({});

  /**
   * This effect is called once, on the first render of the application
   * It checks the environment variables to initialize needed state variables
   * And determines which methods to be using
   */
  React.useEffect(() => {
    async function setUpEcosystem() {
      // For testing and debugging purposes
      console.log("Ecosystem Configuration", config);

      if (config.USE_ELECTRON) {
        // RUNNING ON A LOCAL APP

        // Pass settings to the Electron main process
        await window.electronAPI.setConfig(config);
        await window.electronAPI.setTrigger(trigger);

        setMethod("desktop");
      } else {
        // RUNNING ON THE BROWSER
        if (config.USE_FIREBASE) setMethod("firebase");
        else setMethod("default");
      }
    }
    setUpEcosystem();
  }, []);

  /** VALIDATION FUNCTIONS */
  // Default to valid
  const defaultValidation = async (studyID, participantID, sessionID) => {
    console.log("Validating login: ", { studyID, participantID, sessionID });
    return true;
  };
  // Validate participant/study against Firestore rules
  const firebaseValidation = async (studyID, participantID, sessionID) => {
    console.log("Validating login: ", { studyID, participantID, sessionID });
    const result = await validateParticipant(studyID, participantID);
    return result;
  };

  /** SET METADATA URL FUNCTIONS */
  const defaultSetMetadata = async (studyID) => {
    console.log("Setting default Metadata URL: ", { studyID });

    // If REACT_APP_MISSION_NUMBER is set, use local metadata for that mission
    if (process.env.REACT_APP_MISSION_NUMBER) {
      console.log("Using local metadata for mission:", process.env.REACT_APP_MISSION_NUMBER);
      const chosenMetadata = getLocalMetadata();
      // Validate metadata
      const validationResult = validateMetadata(chosenMetadata);
      if (!validationResult.isValid) {
        console.error("❌ Metadata validation failed:", validationResult.error);
        return validationResult;
      }

      setMetadata({
        ...(validationResult.normalizedMetadata || chosenMetadata),
        debugMetadataSource: "localmetadata",
      });
      return { isValid: true, error: null };
    }

    // Otherwise, fetch metadata from remote URL (Firebase)
    console.log("Fetching metadata from remote URL");
    try {
      const metadataFile = await loadExternalJSON(METADATA_FILE_URL);
      // Validate metadata
      const validationResult = validateMetadata(metadataFile);
      if (!validationResult.isValid) {
        console.error("❌ Metadata validation failed:", validationResult.error);
        return validationResult;
      }
      setMetadata({
        ...(validationResult.normalizedMetadata || metadataFile),
        debugMetadataSource: "firebase",
      });
      return { isValid: true, error: null };
    } catch (e) {
      console.error("Error loading external metadata:", e);
      return {
        isValid: false,
        error: "Failed to load metadata file. Please check your configuration.",
      };
    }
  };

  const firebaseSetMetadata = async (studyID) => {
    console.log("Setting Metadata URL from Firebase: ", { studyID });
    const metadataUrl = await fetchMetadataUrl(studyID);
    if (!metadataUrl) {
      console.error("Error with metadataURL");
      return {
        isValid: false,
        error: "Study not found or has no metadata configuration. Please check your Study ID.",
      };
    }
    try {
      const metadataFile = await loadExternalJSON(metadataUrl);
      // Validate metadata
      const validationResult = validateMetadata(metadataFile);
      if (!validationResult.isValid) {
        console.error("❌ Metadata validation failed:", validationResult.error);
        return validationResult;
      }
      setMetadata({
        ...(validationResult.normalizedMetadata || metadataFile),
        debugMetadataSource: "firebase",
      });
      return { isValid: true, error: null };
    } catch (e) {
      console.error("Error with metadataURL content", e);
      return {
        isValid: false,
        error: "Failed to load metadata file. Check if the metadata URL is valid.",
      };
    }
  };

  /** DATA WRITE FUNCTIONS */

  // Default to no operation
  const defaultFunction = () => {};
  // Add trial data to Firestore (see src/App/deployments/firebase.js)
  const firebaseUpdateFunction = (data) => {
    addToFirebase(stripDebugOnlyKeyboardFields(data));
  };
  // Execute the 'on_data_update' callback function (see public/electron/main.js)
  const desktopUpdateFunction = async (data) => {
    await window.electronAPI.on_data_update(stripDebugOnlyKeyboardFields(data));
  };

  /** EXPERIMENT FINISH FUNCTIONS */

  // Save the experiment data on the desktop
  const defaultFinishFunction = (data) => {
    data.localSave("csv", "task.csv");
  };
  // Do nothing
  const firebaseFinishFunction = () => {};
  // Execute the 'on_finish' callback function (see public/electron/main.js)
  const desktopFinishFunction = async () => {
    await window.electronAPI.on_finish();
  };

  /**
   * Callback function executed when the user logs in.
   *
   * The study and participant IDs are updated and loggedIn is set to true.
   */
  const handleLogin = React.useCallback((studyID, participantID, sessionID) => {
    setLoginInformation({ studyID, participantID, sessionID });
    setLoggedIn(true);
  }, []);

  if (loggedIn) {
    return (
      <JsPsychExperiment
        loginInformation={loginInformation}
        metadata={metadata}
        dataUpdateFunction={
          {
            desktop: desktopUpdateFunction,
            firebase: firebaseUpdateFunction,
            default: defaultFunction,
          }[currentMethod]
        }
        dataFinishFunction={
          {
            desktop: desktopFinishFunction,
            firebase: firebaseFinishFunction,
            default: defaultFinishFunction,
          }[currentMethod]
        }
      />
    );
  } else {
    return (
      <Login
        validationFunction={
          {
            desktop: defaultValidation,
            default: defaultValidation,
            firebase: firebaseValidation,
          }[currentMethod]
        }
        setMetadataFunction={
          {
            desktop: defaultSetMetadata,
            default: defaultSetMetadata,
            firebase: firebaseSetMetadata,
          }[currentMethod]
        }
        handleLogin={handleLogin}
      />
    );
  }
}
